import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router';
import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, startWith } from 'rxjs/operators';
import { LayoutService, LayoutSettings } from './layout.service';

/** Route data key the layout declaration is read from. */
export const LAYOUT_ROUTE_DATA = 'layoutConfig';

/**
 * Which shell elements a route shows. Each key is the element's visibility, so `false` hides it
 * and `true` shows it. Anything left out keeps its default, which is visible.
 */
export type LayoutConfig = Partial<Record<keyof LayoutSettings, boolean>>;

/**
 * Declares which parts of the shell a route shows, for a route's `data`.
 *
 * This is route data rather than a route `providers` entry on purpose. A `providers` array builds
 * an injector scoped to that route and its children, and the master page sits *above* the router
 * outlet - it could never read it, so the provider would appear to do nothing. Route data is
 * visible from above, which is what makes this work.
 *
 * Nothing has to undo it: the master page recomputes the whole layout on every navigation, so a
 * route that says nothing gets the defaults back automatically - including on the browser back
 * button, a redirect, or a guard bouncing the member elsewhere.
 * Each key is the element's visibility: `footer: false` hides the footer, `footer: true` shows it.
 * Anything not mentioned stays visible.
 * @example
 * {
 *   path: 'checkout',
 *   component: UnifiedCheckoutPageComponent,
 *   data: layoutConfig({ footer: false, mobileNavbar: false }),
 * }
 */
export function layoutConfig(config: LayoutConfig, data: Data = {}): Data {
  return { ...data, [LAYOUT_ROUTE_DATA]: config };
}

/**
 * Keeps the LayoutService in step with the active route's `layoutConfig()` declaration, for the
 * component that hosts the router outlet. Call it from that component's constructor.
 *
 * On every navigation it collects each route's layoutConfig() declaration from the whole chain -
 * so a parent can hide something on behalf of its children - and applies it, restoring anything
 * the new route does not mention.
 */
export function applyRouteLayout(): void {
  const router = inject(Router);
  const route = inject(ActivatedRoute);
  const layoutService = inject(LayoutService);
  const destroyRef = inject(DestroyRef);

  router.events
    .pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(null),
      takeUntilDestroyed(destroyRef)
    )
    .subscribe(() => {
      // Deferred a tick: writing to the layout state during a navigation changes a value the
      // already-checked host is bound to, which trips ExpressionChangedAfterItHasBeenChecked.
      setTimeout(() => layoutService.setConfig(collectLayout(route)));
    });
}

/**
 * Walks the route chain to the active leaf and builds the full settings: every element starts
 * visible, and any route in the chain setting one to false hides it. Rebuilding rather than
 * patching is what makes leaving a route restore the shell without anything having to undo itself.
 */
function collectLayout(root: ActivatedRoute): LayoutSettings {
  const settings: LayoutSettings = {
    mobileNavbar: { hidden: false },
    desktopNavbar: { hidden: false },
    footer: { hidden: false },
  };

  let current: ActivatedRoute | null = root;
  while (current) {
    const config = current.snapshot.data[LAYOUT_ROUTE_DATA] as LayoutConfig | undefined;
    if (config) {
      (Object.keys(settings) as (keyof LayoutSettings)[]).forEach((key) => {
        // Only an explicit false hides: an omitted key must leave the element alone rather than
        // read as "not visible".
        if (config[key] === false) {
          settings[key] = { hidden: true };
        }
      });
    }
    current = current.firstChild;
  }

  return settings;
}
